import { Card, Col, Row } from "react-bootstrap";
import HippoTable from "components/ReactTable/HipposReactTable";
import { useContext, useEffect, useState } from "react";
import { apiService } from "service/ApiService";
import StoreContext from "contexts/StoreContext";
import ShowModalButton from "views/Components/ShowModalButton";
import TimestampColumn from "components/Custom/TimestampColumn";
import OrderProductsModal from "components/Modals/OrderProductsModal";
import ExportToExcelButton from "views/Components/ExportToExcelButton";
import { exportToExcel, serverTimesampToNormalDate } from "utils/AppUtils";
import DateRangeContext from "contexts/DateRangeContext";
import { ClipLoader } from "react-spinners";

import { useTranslation } from "react-i18next";

function OrdersTable({ sendData }) {
  const {
    t,
  } = useTranslation();

  const { storeId, setStoreId } = useContext(StoreContext);
  const { dateRange, setDateRange } = useContext(DateRangeContext);
  const [rawOrders, setRawOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [show, setShow] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [modalHeader, setModalHeader] = useState("");
  const [excelLoading, setExcelLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  const handleClose = () => setShow(false);
  const handleShow = (el) => {
    console.log(el);
    setOrderId(el.id);
    setModalHeader(`[${serverTimesampToNormalDate(el.rawLocalCreatedAt)}] #${el.friendlyId} (${el.localId}) ${el.storeUser.name} ${el.total} €`);
    setShow(true);
  };

  const columns = [
    {
      Header: "ID",
      accessor: "complexId",
      maxWidth: 60,
      minWidth: 60,
      width: 60,
    },
    {
      Header: t("tables.headers.createdAt"),
      accessor: "localCreatedAt",
      minWidth: 200,
      maxWidth: 400,
      width: 500,
    },
    {
      Header: t("tables.headers.mode"),
      accessor: "mode",
      maxWidth: 90,
      minWidth: 30,
      width: 80,
    },
    {
      Header: t("tables.headers.user"),
      accessor: "userName",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.table"),
      accessor: "tableName",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.qty"),
      accessor: "quantity",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.opening"),
      accessor: "subtotal",
      disableFilters: true,
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.discount"),
      accessor: "discount",
      disableFilters: true,
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.totalEuro"),
      accessor: "total",
      disableFilters: true,
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: "",
      accessor: "actions",
      maxWidth: 80,
      minWidth: 30,
      width: 60,
    },
  ];

  const getData = () => {
    setShowLoader(true);
    apiService
      .post(`api/v1/orders/store/${storeId}`, {
        fromDate: dateRange[0],
        toDate: dateRange[1],
      })
      .then((resp) => {
        console.log(resp.orders);
        setRawOrders(resp.orders);
        attachCustom(_.cloneDeep(resp.orders));
        sendData(resp.counts);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const attachCustom = (data) => {
    const enhanced = data.map((el) => {
      el.userName = el.storeUser ? el.storeUser.name : "";
      el.complexId = `#${el.friendlyId}`;
      el.mode =
        el.modeId == 1 ? (
          <i title="Takeaway" className="nc-icon nc-bag text-success icon-bold"></i>
        ) : el.modeId == 2 ? (
          <i title="Delivery" className="fa fa-bicycle text-danger"></i>
        ) : (
          <i title="Restaurant" className="nc-icon nc-grid-45 text-warning icon-bold"></i>
        );
      el.tableName = el.table ? el.table.name : "";
      el.subtotal = el.subtotal.toFixed(2);
      el.discount = el.discount.toFixed(2);
      el.total = el.total.toFixed(2);
      el.rawLocalCreatedAt = el.localCreatedAt;
      el.localCreatedAt = <TimestampColumn timestamp={el.localCreatedAt} />;
      el.actions = <ShowModalButton handler={() => handleShow(el)} variant="warning" icon="fa fa-list-ul" className="text-warning btn-link" title={t("common.actions.viewProducts")} />;
      return el;
    });

    setOrders(enhanced);
  };

  const formatData = (data) => {
    if (data.length === 0) return [];
    let formattedData = data.map((row) => {
      return {
        ID: row.complexId,
        [t("tables.headers.createdAt")]: serverTimesampToNormalDate(row.localCreatedAt),
        [t("tables.headers.mode")]: row.modeId == 1 ? "Take Away" : row.modeId == 2 ? "Delivery" : "Restaurant",
        [t("tables.headers.quantity")]: row.quantity,
        [t("tables.headers.user")]: row.userName,
        [t("tables.headers.table")]: row.tableName,
        [t("tables.headers.products")]: row.quantity,
        [t("tables.headers.subtotalEuro")]: row.subtotal,
        [t("tables.headers.discount")]: row.discount,
        [t("tables.headers.totalEuro")]: row.total,
      };
    });
    return formattedData;
  };

  const handleExportData = () => {
    setExcelLoading(true);
    exportToExcel(formatData(rawOrders), `orders-${dateRange[0]}-to-${dateRange[1]}`, t("common.labels.orders"));
    setExcelLoading(false);
  };

  useEffect(() => {
    if (!storeId || !dateRange) return;
    getData();
  }, [storeId, dateRange]);

  return (
    <>
      <OrderProductsModal orderId={orderId} show={show} handleClose={handleClose} headerText={modalHeader} />
      <Card className="strpied-tabled-with-hover">
        <Card.Header>
          <Row>
            <Col>
              <Card.Title as="h4">{t("reports.ordersPlaced")}</Card.Title>
            </Col>
          </Row>
          <Row>
            <Col md="10">
              <p className="card-category">{t(
                "messages.showsOrdersPlacedDuringThePeriodDefinedByTheSelectedDates",
              )}</p>
            </Col>
            <Col md="2">{orders.length === 0 ? "" : <ExportToExcelButton handler={handleExportData} disabled={excelLoading} />}</Col>
          </Row>
        </Card.Header>
        <Card.Body className="table-responsive p-0">
          {showLoader ? (
            <div style={{ textAlign: "center", marginTop: "5vh", marginBottom: "5vh" }}>
              <ClipLoader size={50} color={"#ff6fbc"} />
            </div>
          ) : (
            <Col>
              <HippoTable data={orders} columns={columns} className="-striped -highlight primary-pagination" />
            </Col>
          )}
        </Card.Body>
        <Card.Footer>
          <div className="mb-1"></div>
        </Card.Footer>
      </Card>
    </>
  );
}

export default OrdersTable;
